import styles from "./ConversationMessages.module.less";
import MikeMessage from "./components/mikeMessage/MikeMessage";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { useCallback, useContext, useMemo, useState } from "react";
import { Button, Skeleton, Typography } from "antd";
import { NoDataAvailable } from "~/components/noDataAvailable/NoDataAvailable";
import { useLanguage } from "~/hooks/useLanguage";

import MikeMessagesScrollbars from "./components/mikeMessagesScrollbars/MikeMessagesScrollbars";
import {
  getElementMikeId,
  getElementMikeInitialized,
  getElementMikeVisible,
  getMikeElements,
} from "./helpers/copilotMessageElement";
import { getTimezoneIdentifier } from "~/storages/timezone";

type RenderSignalType = { [key: string]: number };

const SIBLINGS_RANGE = 5;

function ConversationMessages() {
  const { formatDate, formatTime, formatMessage } = useLanguage();

  const {
    requests,
    responses,
    requestLoading,
    canDisplayPreviosMessageButton,
    historyLoading,
    getMessageHistory,
    selectedConversation,
  } = useContext(CopilotMessageContext);

  const timezoneIdentifier = useMemo(() => {
    return (
      selectedConversation?.assetId &&
      getTimezoneIdentifier(selectedConversation?.assetId)
    );
  }, [selectedConversation]);

  const [renderSignal, setRenderSignals] = useState<RenderSignalType>({});

  const mikeMessageVisibleChanged = useCallback((target: Element) => {
    const visible = getElementMikeVisible(target);
    if (!visible) return;

    const mikeItems = getMikeElements();

    const targetId = getElementMikeId(target);

    const targetIndex = mikeItems.findIndex(
      (x) => getElementMikeId(x) === targetId,
    );

    const renderSignals: RenderSignalType = {};

    if (!getElementMikeInitialized(target)) {
      renderSignals[targetId] = 0;
    }

    if (
      targetIndex > SIBLINGS_RANGE &&
      mikeItems[targetIndex - 1] &&
      !getElementMikeVisible(mikeItems[targetIndex - 1])
    ) {
      mikeItems.forEach((x, i) => {
        if (
          getElementMikeInitialized(x) &&
          i < targetIndex - SIBLINGS_RANGE &&
          !getElementMikeVisible(x)
        ) {
          const id = getElementMikeId(x);
          renderSignals[id] = Date.now();
        }
      });
    }

    if (
      targetIndex < mikeItems.length - SIBLINGS_RANGE &&
      mikeItems[targetIndex + 1] &&
      !getElementMikeVisible(mikeItems[targetIndex + 1])
    ) {
      mikeItems.forEach((x, i) => {
        if (
          getElementMikeInitialized(x) &&
          i > targetIndex + SIBLINGS_RANGE &&
          !getElementMikeVisible(x)
        ) {
          const id = getElementMikeId(x);
          renderSignals[id] = Date.now();
        }
      });
    }

    if (Object.keys(renderSignals).length > 0)
      setRenderSignals((p) => ({ ...p, ...renderSignals }));
  }, []);

  return (
    <div className={styles.ConversationMessages}>
      <MikeMessagesScrollbars>
        {canDisplayPreviosMessageButton && !requestLoading ? (
          <div className={styles.buttonContainer}>
            {historyLoading ? (
              <Skeleton active />
            ) : requestLoading ? null : (
              <Button
                onClick={() => !historyLoading && getMessageHistory()}
                loading={historyLoading}
              >
                <Typography.Title level={3}>
                  Show Previous Messages
                </Typography.Title>
              </Button>
            )}
          </div>
        ) : null}

        {requestLoading ? (
          <Skeleton active />
        ) : !requestLoading && requests.length == 0 ? (
          <NoDataAvailable
            style={{ height: 300 }}
            text={formatMessage("ConversationMessages.NoAnalysis")}
          />
        ) : (
          responses.map(
            ({
              id,
              actionType,
              params,
              operationDate,
              greetingMessages,
              recommendationMessages,
              observationMessages,
              checkMessages,
              feedbackSummary,
            }) => (
              <MikeMessage
                key={id}
                id={id}
                type={actionType}
                parameters={params}
                operationDate={`${formatDate(operationDate, {
                  timeZone: timezoneIdentifier,
                })} ${formatTime(operationDate, {
                  timeZone: timezoneIdentifier,
                })}`}
                greetingMessages={greetingMessages}
                observationMessages={observationMessages}
                checkMessages={checkMessages}
                recommendationMessages={recommendationMessages}
                onVisibleChanged={mikeMessageVisibleChanged}
                forceRenderSignal={renderSignal[id]}
                conversationId={selectedConversation?.id || ""}
                feedbackSummary={feedbackSummary}
              />
            ),
          )
        )}
      </MikeMessagesScrollbars>
    </div>
  );
}

export default ConversationMessages;
